import { isNil } from 'lodash';
import { getLogos, postNewLogo, deleteLogo as deleteLogoAPI } from '../common/callAPIs';
import { useEffect, useState } from 'react';
import { replaceS3WithCF } from '../common/replaceS3WithCF';
import { updateLogoSelect, deleteLogo as deleteLogoFromScene } from '../js/webGL';
import { setLoading } from '../redux-tool/slice/uiSlice';
import { useDispatch } from 'react-redux';

// Get resolution from a File without touching the DOM
function getImageResolution(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const result = { width: img.naturalWidth, height: img.naturalHeight };
      console.log('----------RESULT---------------', result);
      URL.revokeObjectURL(url);
      resolve(result);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

export function useLoadLogos(queryInfo, productInfo, product) {
  const [logoList, setLogoList] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isNil(product)) return;
    loadLogos();
  }, [product]);

  const loadLogos = async () => {
    getLogos(queryInfo, productInfo)
      .then((res) => {
        if (res.status !== 200) throw new Error('Fail to load logos');

        return res.json();
      })
      .then((data) => {
        if (!data) throw new Error('Some thing went wrong');

        const logos = data?.map((item) => {
          const temp = product?.design_hub_object?.logos?.find((i) => i?.Id === item?.element_id);
          return {
            Id: item?.element_id,
            Thumbnail: replaceS3WithCF(item?.thumbnail),
            Scale: item?.scale,
            Rotate: item?.rotate,
            ThumbnailImg: replaceS3WithCF(item?.thumbnail_img || item?.thumbnail),
            IsPattern: item?.type_element === 1,
            isUserUploadedLogo: item?.customer_id !== 0,
            ...temp,
          };
        });

        setLogoList(logos);
      })
      .catch((error) => {
        console.log('ERROR WHEN LOADING PRODUCT', error);
      });
  };

  const processAddNewLogo = async (e) => {
    try {
      if (e.target?.files?.length > 0) {
        const file = e.target.files[0];
        await getImageResolution(file);
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64String = reader.result;
          if (base64String) {
            const [typePart, valuePart] = base64String.split(',');
            const type = typePart.match(/:(.+);/)[1];

            const body = {
              design_data: {
                type: 'logo',
                extension: type.split('/')[1],
                content_file: valuePart || '',
              },
            };

            const res = await postNewLogo(queryInfo, productInfo, body);
            const result = await res.json();

            const logo = {
              Id: result?.element_data?.element_id,
              Thumbnail: result?.element_data?.thumbnail,
              Scale: result?.element_data?.scale,
              Rotate: result?.element_data?.rotate,
              file: result?.element_data?.thumbnail,
              IsPattern: false,
              isUserUploadedLogo: true, // Mark as user uploaded
            };

            setLogoList((prev) => [logo, ...prev]);
            updateLogoSelect(logo);
          }
          dispatch(setLoading(false));
        };

        reader.readAsDataURL(file);
      } else {
        dispatch(setLoading(false));
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      dispatch(setLoading(false));
    }
  };

  const handleAddNewLogo = (e) => {
    // Defer processing logic so React can render first
    setTimeout(() => {
      processAddNewLogo(e);
    }, 0);
  };

  const handleDeleteLogo = async (logo) => {
    if (!logo || !logo.Id) return;

    try {
      dispatch(setLoading(true));

      // Call API to delete logo
      const res = await deleteLogoAPI(queryInfo, productInfo, logo.Id);
      const result = await res.json();

      if (result.status) {
        // Remove logo from list
        setLogoList((prev) => prev.filter((l) => l.Id !== logo.Id));

        // Remove logo from 3D scene if it exists
        if (logo.decalMesh) {
          deleteLogoFromScene(logo);
        }

        // If deleted logo was current logo, reset current logo
        if (window.currentLogo && window.currentLogo.Id === logo.Id) {
          window.currentLogo = null;
          if (window.resetActiveLogoIndex && typeof window.resetActiveLogoIndex === 'function') {
            window.resetActiveLogoIndex();
          }
        }
      } else {
        console.error('Failed to delete logo:', result);
      }
    } catch (error) {
      console.error('Error deleting logo:', error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return { logoList, handleAddNewLogo, handleDeleteLogo };
}
